/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  FaArrowLeft,
  FaMicrophone,
  FaStop,
  FaStopwatch,
  FaCheckCircle,
  FaRedo,
  FaPaintBrush,
  FaExpand,
  FaCompress,
  FaChevronLeft,
  FaChevronRight,
  FaInfoCircle,
  FaTimes,
  FaSpinner,
} from 'react-icons/fa';
import { MdKeyboardVoice, MdTranscribe } from 'react-icons/md';
import { HiSparkles } from 'react-icons/hi2';
import { BiSolidMicrophone } from 'react-icons/bi';
import Webcam from 'react-webcam';
import { useNavigate, useParams } from 'react-router-dom';
import { FormInputs } from '../../../types/session-forms.types';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firebase-firestore';
import { Collections } from '../../../types/collection';
import api from '../../../libs/axiosInstance';
import * as faceapi from 'face-api.js';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useUserContext } from '../../../contexts/user.context';
import { INTERVIEW_TYPES } from '../../../pages/NewSession';
import TalkingPointsView from '../components/TalkingPoints';
import Whiteboard from '../components/Whiteboard';
import useMediaRecorder from '@wmik/use-media-recorder';
import Confetti from 'react-confetti';
import { sessionCompletionService } from '../../../services/session-completion.service';
import { captureException } from '@sentry/react';
import { apiUsageService } from '../../../services/api-usage.service';
import { RunData } from '../../../interfaces/run.interface';
import { TalkingPoint } from '../../../interfaces/talking-point.interface';
import { motion, AnimatePresence } from 'framer-motion';
import { useHotkeys } from 'react-hotkeys-hook';
import { useWindowSize } from 'react-use';

// SpeechRecognition shim
const SpeechRecognitionConstructor =
  window.SpeechRecognition || (window as any).webkitSpeechRecognition;

function canRecordScreen() {
  return (
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  );
}

export interface ExpressionData {
  angry: number;
  disgusted: number;
  fearful: number;
  happy: number;
  neutral: number;
  sad: number;
  surprised: number;
}

const WhiteboardActiveSession: React.FC = () => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const { user, reloadUserData } = useUserContext();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { sessionId, runId } = useParams();
  const [supported, setSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setSupported(canRecordScreen());
    setIsLoading(false);
  }, []);

  const [sessionData, setSessionData] = useState<FormInputs>();
  const [talkingPoints, setTalkingPoints] = useState<TalkingPoint[]>([]);

  // Speech Recognition states
  const [transcript, setTranscript] = useState('');
  const [showTalkingPoints, setShowTalkingPoints] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Facial Analysis
  const [expressions, setExpressions] = useState<ExpressionData[]>([]);
  const webcamRef = useRef<Webcam>(null);
  const analysisIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Timer states
  const MAX_RECORDING_TIME_MS =
    Number(user?.subscription.recordingLength) * 60 * 1000;
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(
    MAX_RECORDING_TIME_MS / 1000
  );
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('basic-canvas');
  const [embedUrl, setEmbedUrl] = useState<string>('');
  const [focusWhiteboard, setFocusWhiteboard] = useState<boolean>(false);
  const [submitting, toggleSubmitting] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [hideTranscriptDuringRecording, setHideTranscriptDuringRecording] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPreparingRecording, setIsPreparingRecording] = useState(false);

  // useMediaRecorder setup
  const {
    status,
    startRecording,
    stopRecording,
    mediaBlob,
  } = useMediaRecorder({
    recordScreen: !isMobile,
    blobOptions: { type: 'video/mp4' },
    mediaStreamConstraints: { audio: true, video: true },
  });

  // For previewing the recorded video
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Completion dialog
  const [showRunCompleteDialog, toggleShowRunCompleteDialog] = useState(false);

  // Audio analysis
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  // Prevent multiple startRecording calls during the same preparation phase
  const recordingInitiatedRef = useRef<boolean>(false);

  /* -----------------------------------------------------
   * Helper: Format time for countdown
   * ----------------------------------------------------- */
  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  /* -----------------------------------------------------
   * Reload user data, load face-api models, etc.
   * ----------------------------------------------------- */
  useEffect(() => {
    reloadUserData();
  }, [reloadUserData]);

  /* -----------------------------------------------------
   * CLEANUP: Stop all media streams on component unmount
   * This prevents the camera indicator from staying on after navigation
   * ----------------------------------------------------- */
  useEffect(() => {
    return () => {
      // Stop webcam stream
      if (webcamRef.current?.video?.srcObject) {
        const stream = webcamRef.current.video.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }

      // Also check if we have a direct reference to stream in webcamRef properties
      // Note: react-webcam doesn't always expose stream directly on the ref, but we use it in startRecognition
      // so we should try to clean up if we can find it

      // Stop media recorder if active
      if (status === 'recording' || status === 'paused') {
        stopRecording();
      }

      // Stop speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors
        }
      }

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      // Clear all intervals
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [status, stopRecording]);

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = '/models';
      await faceapi.loadTinyFaceDetectorModel(MODEL_URL);
      await faceapi.loadFaceExpressionModel(MODEL_URL);
    };
    loadModels();
  }, []);

  /* -----------------------------------------------------
   * Fetch Session Data
   * ----------------------------------------------------- */
  useEffect(() => {
    async function getSessionData() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId || !sessionId) return;

        const sessionObjectSnap = await firebase
          .firestore()
          .collection(Collections.Sessions)
          .doc(userId)
          .collection('list')
          .doc(sessionId)
          .get();

        if (!sessionObjectSnap.exists) return;
        const data = sessionObjectSnap.data() as FormInputs;
        setSessionData({ ...data, id: sessionObjectSnap.id });
      } catch (error) {
        captureException(error);
      }
    }
    getSessionData();
  }, [sessionId]);

  /* -----------------------------------------------------
   * Fetch Run Data (Talking Points, etc.)
   * ----------------------------------------------------- */
  useEffect(() => {
    async function getSessionQuestions() {
      try {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId || !sessionId || !runId) return;

        firebase
          .firestore()
          .collection(Collections.Runs)
          .doc(sessionId)
          .collection('list')
          .doc(runId)
          .onSnapshot((snapshot) => {
            if (!snapshot.exists) return;
            const runData = snapshot.data() as RunData;
            if (runData.completed) {
              toggleShowRunCompleteDialog(true);
            }
            if (runData?.talkingPoints) {
              setTalkingPoints(runData.talkingPoints);
            }
          });
      } catch (error) {
        captureException(error);
      }
    }
    getSessionQuestions();
  }, [sessionId, runId]);

  /* -----------------------------------------------------
   * Initialize SpeechRecognition
   * ----------------------------------------------------- */
  useEffect(() => {
    if (!SpeechRecognitionConstructor) {
      captureException('SpeechRecognition is not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognitionConstructor();
    recognition.lang = window.navigator.language || 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptChunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptChunk;
        } else {
          interimTranscript += transcriptChunk;
        }
      }
      setTranscript(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      captureException(event.error);
    };

    recognitionRef.current = recognition;
  }, []);

  /* -----------------------------------------------------
   * Audio Level Analysis
   * ----------------------------------------------------- */
  const setupAudioAnalyzer = useCallback((stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

      const updateAudioLevel = () => {
        if (!analyserRef.current || !dataArrayRef.current) return;

        analyserRef.current.getByteTimeDomainData(dataArrayRef.current as any);

        let sum = 0;
        for (let i = 0; i < dataArrayRef.current.length; i++) {
          const value = (dataArrayRef.current[i] - 128) / 128;
          sum += value * value;
        }
        const rms = Math.sqrt(sum / dataArrayRef.current.length);

        const normalizedLevel = Math.min(rms * 1000, 100);
        setAudioLevel(normalizedLevel);
        setIsSpeaking(rms > 0.01);

        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();
    } catch (error) {
      console.error('Error setting up audio analyzer:', error);
    }
  }, []);

  /* -----------------------------------------------------
   * Face Analysis
   * ----------------------------------------------------- */
  const startExpressionAnalysis = useCallback(() => {
    if (analysisIntervalRef.current) return;
    analysisIntervalRef.current = setInterval(async () => {
      if (
        webcamRef.current?.video &&
        webcamRef.current.video.readyState === 4 &&
        faceapi.nets.tinyFaceDetector.params
      ) {
        const detections = await faceapi
          .detectAllFaces(webcamRef.current.video, new faceapi.TinyFaceDetectorOptions())
          .withFaceExpressions();
        if (detections && detections.length > 0) {
          setExpressions((prev) => [
            ...prev,
            detections[0].expressions as ExpressionData,
          ]);
        }
      }
    }, 1000);
  }, []);

  const stopExpressionAnalysis = useCallback(() => {
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
    }
  }, []);

  /* -----------------------------------------------------
   * Submit Answer
   * ----------------------------------------------------- */
  const submitAnswer = useCallback(async () => {
    try {
      if (!sessionData) return;

      toast.current?.show({
        severity: 'info',
        summary: 'Uploading...',
        detail: 'Saving your recording and generating feedback',
      });

      const userId = firebase.auth().currentUser?.uid;
      if (!userId) return;
      if (!mediaBlob) return;
      toggleSubmitting(true);

      const formData = new FormData();
      formData.append('file', mediaBlob, 'recording.webm');
      formData.append('transcript', transcript);
      formData.append('expressions', JSON.stringify(expressions));
      formData.append('userId', userId);
      formData.append('runId', String(runId));
      formData.append('sessionId', String(sessionData.id));
      formData.append('interviewType', INTERVIEW_TYPES.WHITEBOARD_SESSION);
      formData.append('sessionTitle', sessionData.title);

      const response = await api.post('/ai/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if ([200, 201].includes(response.status)) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success!',
          detail: 'Your response is being analyzed',
        });
        toggleSubmitting(false);
        endSession();
      }
      toggleSubmitting(false);
    } catch (error) {
      toggleSubmitting(false);
      captureException(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: 'Could not upload your recording. Please try again.',
      });
    }
  }, [sessionData, runId, mediaBlob, transcript, expressions]);

  /* -----------------------------------------------------
   * Start / Stop Logic
   * ----------------------------------------------------- */
  const isRecording = status === 'recording';
  // Use isPreparingRecording OR isRecording to hide UI elements
  const isRecordingOrPreparing = isPreparingRecording || isRecording;

  const stopRecognition = useCallback(() => {
    // Reset preparing state and recording initiated flag
    setIsPreparingRecording(false);
    recordingInitiatedRef.current = false;

    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);

    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    stopExpressionAnalysis();
    stopRecording();

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setAudioLevel(0);
    setIsSpeaking(false);
  }, [isListening, MAX_RECORDING_TIME_MS, stopRecording, stopExpressionAnalysis]);

  const startRecognition = useCallback(() => {
    // Hide UI elements FIRST before starting recording
    setIsPreparingRecording(true);
  }, []);

  // Effect to start actual recording after UI elements are hidden
  useEffect(() => {
    // Guard: Only run once per preparation phase
    if (isPreparingRecording && !isRecording && !recordingInitiatedRef.current) {
      // Mark as initiated to prevent multiple calls
      recordingInitiatedRef.current = true;

      // Small delay to ensure UI has updated
      const startTimer = setTimeout(() => {
        setTranscript('');
        setExpressions([]);
        setVideoUrl(null);
        setShowVideoPreview(false);

        if (recognitionRef.current && !isListening) {
          recognitionRef.current.start();
          setIsListening(true);
        }

        startRecording();

        // Setup audio analysis if webcam is available
        if (webcamRef.current?.stream) {
          setupAudioAnalyzer(webcamRef.current.stream);
        }

        startExpressionAnalysis();

        if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
        if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);

        setRecordingTimeLeft(MAX_RECORDING_TIME_MS / 1000);
        recordingIntervalRef.current = setInterval(() => {
          setRecordingTimeLeft((prev) => {
            if (prev <= 1) {
              stopRecognition();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        recordingTimeoutRef.current = setTimeout(() => {
          stopRecognition();
          submitAnswer();
        }, MAX_RECORDING_TIME_MS);
      }, 300); // 300ms delay to let UI update

      return () => clearTimeout(startTimer);
    }
  }, [isPreparingRecording, isRecording, isListening, MAX_RECORDING_TIME_MS, setupAudioAnalyzer, startRecording, stopRecognition, startExpressionAnalysis, submitAnswer]);

  // Reset preparing state when recording actually starts
  useEffect(() => {
    if (isRecording) {
      setIsPreparingRecording(false);
    }
  }, [isRecording]);

  const handleRecordAgain = useCallback(() => {
    startRecognition();
  }, [startRecognition]);

  /* -----------------------------------------------------
   * Watch for mediaBlob once recording stops
   * ----------------------------------------------------- */
  useEffect(() => {
    if (status === 'stopped' && mediaBlob) {
      const videoURL = URL.createObjectURL(mediaBlob);
      setVideoUrl(videoURL);
      setShowVideoPreview(true);
    }
  }, [status, mediaBlob]);

  /* -----------------------------------------------------
   * End Session
   * ----------------------------------------------------- */
  const endSession = async () => {
    try {
      const userId = firebase.auth().currentUser?.uid;
      if (!userId || !sessionId || !runId) return;

      toggleShowRunCompleteDialog(true);

      await firebase
        .firestore()
        .collection(Collections.Runs)
        .doc(sessionId)
        .collection('list')
        .doc(runId)
        .update({
          completed: true,
          status: 'Completed',
          currentQuestion: 0,
        });

      await firebase
        .firestore()
        .collection(Collections.Sessions)
        .doc(userId)
        .collection('list')
        .doc(sessionId)
        .update({
          completed: true,
          status: 'Completed',
        });

      await apiUsageService.incrementPracticeAttemptUsage();

      try {
        await sessionCompletionService.notifySessionCompletion({
          sessionId: sessionId,
          userId: userId,
          runId: runId,
          overallScore: 0,
          sessionType: 'whiteboard_session',
          completedAt: new Date(),
          skills: {}
        });
      } catch (goalError) {
        console.error('Error updating goals from session completion:', goalError);
      }

      setTranscript('');
      setVideoUrl(null);
    } catch (error) {
      captureException(error);
    }
  };

  // Keyboard shortcuts
  useHotkeys('space', (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecognition();
    } else if (!submitting && !videoUrl) {
      startRecognition();
    }
  }, [isRecording, submitting, videoUrl, startRecognition, stopRecognition]);

  useHotkeys('escape', (e) => {
    if (showRunCompleteDialog) {
      e.preventDefault();
      toggleShowRunCompleteDialog(false);
    } else if (showTalkingPoints) {
      e.preventDefault();
      setShowTalkingPoints(false);
    } else if (isRecording) {
      e.preventDefault();
      stopRecognition();
    }
  }, [isRecording, showRunCompleteDialog, showTalkingPoints, stopRecognition]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
        <div className="text-center">
          <DotLottieReact
            src="https://lottie.host/f7b5bebc-ce92-4136-9295-815dea9c1920/P0BpzInq7K.lottie"
            loop
            autoplay
            className="w-24 h-24 mx-auto mb-4"
          />
          <p className="text-gray-600">Loading whiteboard session...</p>
        </div>
      </div>
    );
  }

  if (!supported || isMobile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50 text-center p-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md"
        >
          <DotLottieReact
            src="https://lottie.host/94ed8909-b2b7-4e2b-857b-d6aab6e308f8/57SiUJfbEf.lottie"
            loop
            autoplay
            className="w-64 h-64 mb-6 mx-auto"
          />
          <h2 className="text-3xl font-bold mb-3 text-gray-800">Desktop Required</h2>
          <p className="mb-6 text-gray-600 leading-relaxed">
            Whiteboard sessions require screen sharing and are best experienced on a desktop or laptop computer.
            <br />
            <span className="text-sm text-gray-500">Please switch to a desktop device for the full experience.</span>
          </p>
          <motion.button
            onClick={() => navigate(-1)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition shadow-lg font-semibold"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaArrowLeft className="inline mr-2" />
            Go Back
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
        <DotLottieReact
          src="https://lottie.host/f7b5bebc-ce92-4136-9295-815dea9c1920/P0BpzInq7K.lottie"
          loop
          autoplay
          className="w-32 h-32"
        />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 text-center"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-2">Uploading Your Recording</h3>
          <p className="text-gray-600">Please wait while we process your session...</p>
        </motion.div>
      </div>
    );
  }

  if (!sessionData) return null;

  const progressPercentage = ((MAX_RECORDING_TIME_MS / 1000 - recordingTimeLeft) / (MAX_RECORDING_TIME_MS / 1000)) * 100;
  const timeWarning = recordingTimeLeft <= 30;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      <Toast ref={toast} />

      {/* Modern Sticky Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-md border-b border-gray-200"
      >
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <motion.button
                onClick={() => navigate(-1)}
                disabled={submitting || isRecordingOrPreparing}
                className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-lg hover:bg-gray-100"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaArrowLeft className="text-lg sm:text-xl" />
              </motion.button>

              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                  {sessionData.title}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                    <FaStopwatch className="text-xs" />
                    {sessionData.duration?.label}
                  </span>
                  <span className="text-xs text-gray-400">•</span>
                  <span className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                    <FaPaintBrush className="text-xs" />
                    Whiteboard Session
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <AnimatePresence>
                {!isRecordingOrPreparing && (
                  <>
                    <motion.button
                      initial={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0, margin: 0, padding: 0 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => setShowTalkingPoints(true)}
                      disabled={submitting}
                      className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-lg hover:from-purple-200 hover:to-pink-200 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <HiSparkles className="text-base" />
                      <span className="hidden lg:inline">Talking Points</span>
                    </motion.button>

                    <motion.button
                      initial={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0, margin: 0, padding: 0 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => setFocusWhiteboard((prev) => !prev)}
                      disabled={submitting}
                      className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {focusWhiteboard ? (
                        <>
                          <FaCompress className="text-base" />
                          <span className="hidden lg:inline">Side View</span>
                        </>
                      ) : (
                        <>
                          <FaExpand className="text-base" />
                          <span className="hidden lg:inline">Focus Mode</span>
                        </>
                      )}
                    </motion.button>
                  </>
                )}
              </AnimatePresence>

              <motion.button
                onClick={endSession}
                disabled={submitting || isRecordingOrPreparing}
                className="px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="hidden sm:inline">End Session</span>
                <span className="sm:hidden">End</span>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Recording Status Bar */}
        {isRecordingOrPreparing && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 sm:px-6 lg:px-8 py-2"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="w-3 h-3 bg-white rounded-full"
                />
                <span className="font-semibold text-sm sm:text-base">
                  {isRecording ? 'Recording in progress...' : 'Preparing recording...'}
                </span>
              </div>
              <div className="flex items-center gap-4">
                {isRecording ? (
                  <div className="text-right">
                    <div className={`font-bold text-lg sm:text-xl ${timeWarning ? 'text-yellow-200' : ''}`}>
                      {formatTime(recordingTimeLeft)}
                    </div>
                    <div className="text-xs opacity-90">Time remaining</div>
                  </div>
                ) : (
                  <div className="text-right">
                    <div className="font-bold text-lg sm:text-xl">Please wait...</div>
                    <div className="text-xs opacity-90">Setting up recording</div>
                  </div>
                )}
              </div>
            </div>
            {isRecording && (
              <div className="mt-2 h-1.5 bg-white/30 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full ${timeWarning ? 'bg-yellow-300' : 'bg-white'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 1, ease: 'linear' }}
                />
              </div>
            )}
          </motion.div>
        )}
      </motion.header>

      {/* Main Content Area */}
      <main className="flex-1 relative overflow-hidden">
        {focusWhiteboard ? (
          /* Focus Mode Layout - Whiteboard Full Screen */
          <div className="relative w-full h-[calc(100vh-80px)]">
            {/* Whiteboard takes full screen */}
            <div className="absolute inset-0">
              <Whiteboard
                selectedPlatform={selectedPlatform}
                setSelectedPlatform={setSelectedPlatform}
                embedUrl={embedUrl}
                setEmbedUrl={setEmbedUrl}
                isFocused={focusWhiteboard}
                isRecording={isRecordingOrPreparing}
              />
            </div>

            {/* Floating Control Panel */}
            <motion.div
              initial={{ x: isCollapsed ? 0 : 400 }}
              animate={{ x: isCollapsed ? 0 : 0 }}
              className={`absolute top-4 right-4 z-40 bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-200 ${isCollapsed ? 'w-16' : 'w-80 sm:w-96'}`}
            >
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="absolute -left-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg border border-gray-200 hover:bg-gray-50 transition"
              >
                {isCollapsed ? (
                  <FaChevronLeft className="text-gray-600" />
                ) : (
                  <FaChevronRight className="text-gray-600" />
                )}
              </button>

              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 space-y-4"
                >
                  {/* Camera Feed */}
                  <div className="relative rounded-xl overflow-hidden bg-gray-900">
                    <Webcam
                      audio
                      className="w-full h-auto object-cover"
                      ref={webcamRef}
                      muted
                      mirrored
                      style={{ maxHeight: '180px' }}
                    />
                    {isRecording && (
                      <div className="absolute top-2 left-2 flex items-center gap-2 bg-red-500 text-white px-2 py-1 rounded-lg text-xs font-semibold">
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 1 }}
                          className="w-2 h-2 bg-white rounded-full"
                        />
                        LIVE
                      </div>
                    )}
                    {isSpeaking && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ repeat: Infinity, duration: 0.5 }}
                        className="absolute bottom-2 right-2 bg-green-500 text-white p-2 rounded-full"
                      >
                        <MdKeyboardVoice className="text-lg" />
                      </motion.div>
                    )}
                  </div>

                  {/* Recording Controls */}
                  <div className="space-y-3">
                    {isRecording ? (
                      <motion.button
                        onClick={stopRecognition}
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaStop className="text-base" />
                        Stop Recording
                      </motion.button>
                    ) : videoUrl ? (
                      <div className="space-y-2">
                        <motion.button
                          onClick={submitAnswer}
                          disabled={submitting}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <FaCheckCircle className="text-base" />
                          Submit Session
                        </motion.button>
                        <motion.button
                          onClick={handleRecordAgain}
                          disabled={submitting}
                          className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <FaRedo className="text-base" />
                          Record Again
                        </motion.button>
                      </div>
                    ) : (
                      <motion.button
                        onClick={startRecognition}
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaMicrophone className="text-base" />
                        Start Recording
                      </motion.button>
                    )}

                    {/* Timer Display */}
                    {isRecording && (
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-3 rounded-xl border border-red-200">
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 ${timeWarning ? 'text-red-600' : 'text-gray-800'}`}>
                            {formatTime(recordingTimeLeft)}
                          </div>
                          <div className="text-xs text-gray-600">Time remaining</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Transcript Section */}
                  {showTranscript && transcript && (!isRecordingOrPreparing || !hideTranscriptDuringRecording) && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="bg-gray-50 rounded-xl p-3 max-h-40 overflow-y-auto"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <MdTranscribe className="text-indigo-600" />
                          <span className="font-semibold text-sm text-gray-700">Live Transcript</span>
                        </div>
                        <button
                          onClick={() => setShowTranscript(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {transcript}
                      </p>
                    </motion.div>
                  )}

                  {/* Video Preview */}
                  {videoUrl && showVideoPreview && !isRecording && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="rounded-xl overflow-hidden bg-gray-900"
                    >
                      <video
                        src={videoUrl}
                        controls
                        className="w-full"
                        style={{ maxHeight: '200px' }}
                      />
                    </motion.div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </div>
        ) : (
          /* Side-by-Side Layout */
          <div className="p-4 sm:p-6 lg:p-8 h-[calc(100vh-80px)] overflow-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 h-full max-w-[1920px] mx-auto">
              {/* Left: Whiteboard - Takes 2 columns */}
              <div className="lg:col-span-2 flex flex-col">
                <Whiteboard
                  selectedPlatform={selectedPlatform}
                  setSelectedPlatform={setSelectedPlatform}
                  embedUrl={embedUrl}
                  setEmbedUrl={setEmbedUrl}
                  isFocused={focusWhiteboard}
                  isRecording={isRecordingOrPreparing}
                />
              </div>

              {/* Right: Controls Panel - Takes 1 column */}
              <div className="flex flex-col gap-4">
                {/* Camera Feed Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
                >
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MdKeyboardVoice className="text-lg" />
                      <span className="font-semibold text-sm">Camera Feed</span>
                    </div>
                    {isRecording && (
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        className="w-2 h-2 bg-white rounded-full"
                      />
                    )}
                  </div>
                  <div className="relative bg-gray-900 aspect-video">
                    <Webcam
                      audio
                      className="w-full h-full object-cover"
                      ref={webcamRef}
                      muted
                      mirrored
                    />
                    {isRecording && (
                      <div className="absolute top-2 left-2 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg">
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ repeat: Infinity, duration: 1 }}
                          className="w-2 h-2 bg-white rounded-full"
                        />
                        RECORDING
                      </div>
                    )}
                    {isSpeaking && audioLevel > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ repeat: Infinity, duration: 0.5 }}
                        className="absolute bottom-2 right-2 bg-green-500 text-white p-2 rounded-full shadow-lg"
                      >
                        <BiSolidMicrophone className="text-lg" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                {/* Controls Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-200 space-y-4"
                >
                  {/* Recording Controls */}
                  <div className="space-y-3">
                    {isRecording ? (
                      <motion.button
                        onClick={stopRecognition}
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaStop className="text-base" />
                        Stop Recording
                      </motion.button>
                    ) : videoUrl ? (
                      <div className="space-y-2">
                        <motion.button
                          onClick={submitAnswer}
                          disabled={submitting}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {submitting ? (
                            <>
                              <FaSpinner className="animate-spin text-base" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FaCheckCircle className="text-base" />
                              Submit Session
                            </>
                          )}
                        </motion.button>
                        <motion.button
                          onClick={handleRecordAgain}
                          disabled={submitting}
                          className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <FaRedo className="text-base" />
                          Record Again
                        </motion.button>
                      </div>
                    ) : (
                      <motion.button
                        onClick={startRecognition}
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaMicrophone className="text-base" />
                        Start Recording
                      </motion.button>
                    )}

                    {/* Timer */}
                    {isRecording && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-xl border-2 border-red-200"
                      >
                        <div className="text-center">
                          <div className={`text-3xl font-bold mb-1 ${timeWarning ? 'text-red-600 animate-pulse' : 'text-gray-800'}`}>
                            {formatTime(recordingTimeLeft)}
                          </div>
                          <div className="text-xs text-gray-600 mb-2">Time remaining</div>
                          <div className="w-full bg-red-200 rounded-full h-2 overflow-hidden">
                            <motion.div
                              className={`h-full ${timeWarning ? 'bg-red-600' : 'bg-red-500'}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${progressPercentage}%` }}
                              transition={{ duration: 1, ease: 'linear' }}
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Hint */}
                    {!isRecording && !videoUrl && status === 'idle' && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-lg">
                        <div className="flex items-start gap-2">
                          <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-blue-700">
                            <strong>Tip:</strong> When prompted, select <em>"Entire Screen"</em> to record your whiteboard and presentation.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Transcript Section */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MdTranscribe className="text-indigo-600 text-lg" />
                        <span className="font-semibold text-gray-800">Live Transcript</span>
                      </div>
                      <button
                        onClick={() => setShowTranscript(!showTranscript)}
                        className="text-gray-400 hover:text-gray-600 transition"
                      >
                        {showTranscript ? <FaChevronRight /> : <FaChevronLeft />}
                      </button>
                    </div>

                    {/* Hide Transcript During Recording Checkbox */}
                    {!isRecordingOrPreparing && (
                      <div className="mb-3 flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <input
                          type="checkbox"
                          id="hideTranscriptDuringRecording"
                          checked={hideTranscriptDuringRecording}
                          onChange={(e) => setHideTranscriptDuringRecording(e.target.checked)}
                          className="w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 cursor-pointer"
                        />
                        <label
                          htmlFor="hideTranscriptDuringRecording"
                          className="text-xs sm:text-sm text-gray-700 cursor-pointer select-none flex-1"
                        >
                          Hide transcript during recording
                        </label>
                      </div>
                    )}

                    <AnimatePresence>
                      {showTranscript && (!isRecordingOrPreparing || !hideTranscriptDuringRecording) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                            {transcript ? (
                              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                {transcript}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400 italic text-center py-4">
                                Transcript will appear here as you speak...
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {isRecordingOrPreparing && hideTranscriptDuringRecording && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 text-center">
                        Transcript is hidden during recording
                      </div>
                    )}
                  </div>

                  {/* Video Preview */}
                  {videoUrl && showVideoPreview && !isRecording && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-t border-gray-200 pt-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800">Recording Preview</span>
                        <button
                          onClick={() => setShowVideoPreview(!showVideoPreview)}
                          className="text-gray-400 hover:text-gray-600 transition text-sm"
                        >
                          {showVideoPreview ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      <div className="rounded-lg overflow-hidden bg-gray-900">
                        <video
                          src={videoUrl}
                          controls
                          className="w-full"
                          style={{ maxHeight: '200px' }}
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>

                {/* Talking Points Card */}
                {talkingPoints.length > 0 && !isRecordingOrPreparing && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <HiSparkles className="text-lg" />
                        <span className="font-semibold text-sm">Talking Points</span>
                      </div>
                      <button
                        onClick={() => setShowTalkingPoints(true)}
                        className="text-white/90 hover:text-white transition text-xs"
                      >
                        View All
                      </button>
                    </div>
                    <div className="p-3 max-h-48 overflow-y-auto">
                      {talkingPoints.slice(0, 3).map((point: any, index: number) => (
                        <div key={index} className="mb-3 pb-3 border-b border-gray-100 last:border-0">
                          {point.topic && (
                            <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded mb-2">
                              {point.topic}
                            </span>
                          )}
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {Array.isArray(point.talkingPoints) && point.talkingPoints[0]?.section}
                          </p>
                        </div>
                      ))}
                      {talkingPoints.length > 3 && (
                        <button
                          onClick={() => setShowTalkingPoints(true)}
                          className="text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-2"
                        >
                          View {talkingPoints.length - 3} more...
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Talking Points Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <HiSparkles className="text-purple-600" />
            <span>Your Talking Points</span>
          </div>
        }
        visible={showTalkingPoints}
        className="lg:w-[70vw] max-w-4xl"
        onHide={() => setShowTalkingPoints(false)}
        style={{ borderRadius: '1rem' }}
      >
        <TalkingPointsView data={talkingPoints} />
      </Dialog>

      {/* Completion Dialog */}
      <AnimatePresence>
        {showRunCompleteDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => toggleShowRunCompleteDialog(false)}
          >
            <div className="absolute inset-0">
              <Confetti />
            </div>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-2xl shadow-2xl p-8 sm:p-10 text-center max-w-md w-full"
            >
              <DotLottieReact
                src="https://lottie.host/392d3f6c-7239-46fc-9b41-ca5a9814504d/hFXbyazAMW.lottie"
                loop
                autoplay
                className="w-40 h-40 mx-auto mb-6"
              />
              <h2 className="text-3xl font-bold text-gray-800 mb-3">
                Session Complete! 🎉
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Great job completing your whiteboard session! We're analyzing your recording and will generate comprehensive feedback shortly.
              </p>
              <motion.button
                onClick={() => navigate(-1)}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition font-semibold shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Return to Dashboard
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyboard Shortcuts Hint - Desktop Only */}
      {!isMobile && !isRecording && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
          className="fixed bottom-4 left-4 bg-white/95 backdrop-blur-md rounded-lg shadow-lg p-3 border border-gray-200 text-xs text-gray-600 z-40"
        >
          <div className="font-semibold mb-2 text-gray-800">Keyboard Shortcuts:</div>
          <div className="space-y-1">
            <div><kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Space</kbd> Start/Stop recording</div>
            <div><kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Esc</kbd> Close dialogs</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default WhiteboardActiveSession;